import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
}

interface BreadcrumbNavigationProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
}

export const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  items = [],
  showHome = true
}) => {
  const location = useLocation();
  const { t } = useLanguage();

  // Generate breadcrumbs from current path if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs: BreadcrumbItem[] = [];

    if (showHome) {
      breadcrumbs.push({
        label: t('navigation.home'),
        path: '/dashboard',
        isActive: location.pathname === '/dashboard'
      });
    }

    // Map of parent paths that don't have routes to their default child route
    const parentPathDefaults: Record<string, string> = {
      '/templates': '/templates/manage',
      '/tenant/roadmaps': '/tenant/roadmaps',
      '/alignment': '/alignment',
      '/risk': '/risk',
      '/issues': '/issues',
      '/partner': '/partner/directory',
      '/stakeholders': '/stakeholders',
      '/discussions': '/discussions',
      '/benchmarking': '/benchmarking'
    };

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      const label =
        segment === 'performance' || segment === 'business-performance'
          ? t('businessPerformance.dashboardOverview') || 'Performance Dashboard'
          : segment
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

      // If this path has a default child route and it's not the last segment, use the default
      const breadcrumbPath = parentPathDefaults[currentPath] && !isLast 
        ? parentPathDefaults[currentPath] 
        : currentPath;

      breadcrumbs.push({
        label,
        path: breadcrumbPath,
        isActive: isLast
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = items.length > 0 ? items : generateBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="flex items-center space-x-1 text-sm text-[var(--color-secondary-text)] bg-[var(--color-secondary-bg)] py-2 pl-6 w-full">
      {breadcrumbs.map((item, index) => (
        <React.Fragment key={`${item.path}-${index}`}>
          {index === 0 && showHome && (
            <HomeIcon className="w-4 h-4 mr-1 text-[var(--color-secondary-text)]" />
          )}
          
          {item.isActive ? (
            <span className="text-[var(--color-surface-text)] font-medium">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.path}
              className="text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] transition-colors"
            >
              {item.label}
            </Link>
          )}
          
          {index < breadcrumbs.length - 1 && (
            <ChevronRightIcon className="w-4 h-4 mx-1 text-[var(--color-secondary-text)]" />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};